import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { initials } from "@/lib/format";
import { Shuffle, Scales, HandPointing } from "@phosphor-icons/react";

const MODES = [
  { value: "off", label: "Manual only", icon: HandPointing, hint: "Tickets stay unassigned until you pick an assignee." },
  { value: "round_robin", label: "Round-robin", icon: Shuffle, hint: "Each new ticket goes to the next agent in rotation." },
  { value: "least_loaded", label: "Least loaded", icon: Scales, hint: "Assigns to the agent with the fewest open tickets." },
];

export function AutoAssignPanel({ settings, setSettings, users, isAdmin, saving, onSave }) {
  const eligibleAgents = users.filter((u) => u.role === "agent" || u.role === "admin");
  const currentMode = MODES.find((m) => m.value === settings.auto_assign_mode) || MODES[0];

  const togglePool = (id) => {
    setSettings((s) => {
      const has = s.agent_pool.includes(id);
      return { ...s, agent_pool: has ? s.agent_pool.filter((x) => x !== id) : [...s.agent_pool, id] };
    });
  };

  return (
    <div className="hairline rounded-lg p-5 bg-white" data-testid="settings-auto-assign">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="font-heading font-semibold">Ticket assignment</div>
          <p className="text-xs text-slate-500 mt-1">
            Decide how new tickets are routed. Manual assignment via the ticket sidebar always works.
            {!isAdmin && <span className="text-yellow-400"> Admin only — changes disabled.</span>}
          </p>
        </div>
        <span className="chip !text-slate-700 !border-slate-300 bg-slate-100 font-mono">
          <currentMode.icon size={12} weight="duotone" /> {currentMode.label}
        </span>
      </div>

      <div className="mt-4 space-y-3">
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">Mode</Label>
          <Select
            value={settings.auto_assign_mode}
            onValueChange={(v) => setSettings((s) => ({ ...s, auto_assign_mode: v }))}
            disabled={!isAdmin}
          >
            <SelectTrigger data-testid="settings-assign-mode" className="mt-1 bg-[#FFFFFF] border-slate-200 h-9 max-w-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-200">
              {MODES.map((m) => (
                <SelectItem key={m.value} value={m.value}>
                  <div className="flex items-center gap-2"><m.icon size={14} weight="duotone" /> {m.label}</div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-[11px] text-slate-500 mt-1">{currentMode.hint}</div>
        </div>

        {settings.auto_assign_mode !== "off" && (
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500">Assignment pool</Label>
            <div className="text-[11px] text-slate-500 mb-2">Only checked agents receive auto-assigned tickets.</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {eligibleAgents.map((u) => (
                <label
                  key={u.id}
                  data-testid="settings-agent-pool-row"
                  className={`flex items-center gap-3 p-2 rounded-md border border-slate-200 bg-[#FFFFFF] hover:bg-slate-50 transition-colors ${!isAdmin ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                >
                  <Checkbox
                    checked={settings.agent_pool.includes(u.id)}
                    onCheckedChange={() => isAdmin && togglePool(u.id)}
                    disabled={!isAdmin}
                  />
                  <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-[10px]">
                    {u.avatar_url ? <img src={u.avatar_url} alt="" className="w-full h-full rounded-full object-cover" /> : initials(u.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">{u.name}</div>
                    <div className="text-[11px] text-slate-500 font-mono truncate">{u.email}</div>
                  </div>
                  <span className="chip !text-slate-600 !border-slate-200 bg-slate-100 capitalize">{u.role}</span>
                </label>
              ))}
            </div>
            {settings.agent_pool.length === 0 && (
              <div className="text-[11px] text-yellow-400 mt-2">
                ⚠ Pool is empty — new tickets will stay unassigned until you add agents.
              </div>
            )}
          </div>
        )}

        <TeamModeOverrides settings={settings} setSettings={setSettings} isAdmin={isAdmin} />

        {isAdmin && (
          <Button
            data-testid="settings-save-button"
            onClick={onSave}
            disabled={saving}
            className="bg-[#4f4c90] hover:bg-[#5F5CA5] h-9"
          >
            {saving ? "Saving…" : "Save settings"}
          </Button>
        )}
      </div>
    </div>
  );
}


const QUEUES = [
  { key: "it", label: "IT queue" },
  { key: "systems", label: "Business Systems queue" },
];

function TeamModeOverrides({ settings, setSettings, isAdmin }) {
  const overrides = settings.team_auto_assign_mode || {};
  const setMode = (key, value) => {
    setSettings((s) => {
      const next = { ...(s.team_auto_assign_mode || {}) };
      if (value === "__global__") delete next[key]; else next[key] = value;
      return { ...s, team_auto_assign_mode: next };
    });
  };
  return (
    <div className="pt-2 border-t border-slate-100" data-testid="settings-team-mode-overrides">
      <Label className="text-[11px] uppercase tracking-wider text-slate-500">Per-queue override</Label>
      <div className="text-[11px] text-slate-500 mb-2">
        Run a different mode for each queue (e.g. round-robin for Business Systems but manual-only for IT).
        Leave on "Use global" to inherit the mode above. Rules in Assignment rules still take precedence.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {QUEUES.map((q) => {
          const current = overrides[q.key] ?? "__global__";
          return (
            <div key={q.key} className="flex items-center gap-3 p-2 rounded-md border border-slate-200 bg-white">
              <div className="text-sm w-40 shrink-0">{q.label}</div>
              <Select
                value={current}
                onValueChange={(v) => setMode(q.key, v)}
                disabled={!isAdmin}
              >
                <SelectTrigger data-testid={`settings-team-mode-${q.key}`} className="bg-white border-slate-200 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="__global__">Use global</SelectItem>
                  {MODES.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      <div className="flex items-center gap-2"><m.icon size={14} weight="duotone" /> {m.label}</div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        })}
      </div>
    </div>
  );
}
